import { css } from '@emotion/react';
import { BarSeriesOption, EChartsOption } from 'echarts';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { EChartWrapper } from '../../../../components/Charts/EchartWrapper';
import { generateChartColourScheme } from '../../../../components/Charts/generateChartColourScheme';
import { colors } from '../../../../theme/colors';
import { dateToPlainDateString } from '../../../../util/date-utilts';
import { FMT } from '../../../../util/formatter-service';
import {
  IRoundEntry,
  IStackedBar,
  IStackedBarPosition,
  roundsForCompanyJ,
  selectedRoundJ,
  stackedBarsJ,
} from './state';
import { OwnershipTimelineViewMode, ownershipTimelineViewModeJ } from './viewMode';

const STACK_ID = 'holdings';

const chartContainer = css`
  height: 320px;
  width: 100%;
  padding: 0 0.5rem 0.5rem;
`;

const MODE_FORMATTERS: Record<OwnershipTimelineViewMode, { axis: string; value: string }> = {
  ownership: { axis: 'percent0Dp', value: 'percent' },
  shares: { axis: 'numericShort', value: 'integer' },
  value: { axis: 'currencyShort', value: 'currencyShort' },
};

function metricValue(position: IStackedBarPosition, mode: OwnershipTimelineViewMode): number {
  switch (mode) {
    case 'shares':
      return position.shares;
    case 'value':
      return position.fmv;
    default:
      return position.ownershipPct;
  }
}

interface IOwnershipTimelineChartProps {
  companyId: number;
}

export function OwnershipTimelineChart({ companyId }: IOwnershipTimelineChartProps) {
  const bars = useAtomValue(stackedBarsJ(companyId));
  const rounds = useAtomValue(roundsForCompanyJ(companyId));
  const selected = useAtomValue(selectedRoundJ(companyId));
  const mode = useAtomValue(ownershipTimelineViewModeJ(companyId));

  const option = useMemo(
    () => buildOwnershipChartOption(bars, rounds, selected?.key ?? null, mode),
    [bars, rounds, selected, mode]
  );

  return (
    <div css={chartContainer}>
      {/* Merge (not notMerge) so ECharts animates between option updates — stepping the
          as-of-date slides the highlight instead of hard-resetting the chart. */}
      <EChartWrapper options={option} />
    </div>
  );
}

export function buildOwnershipChartOption(
  bars: IStackedBar[],
  rounds: IRoundEntry[],
  selectedKey: string | null,
  mode: OwnershipTimelineViewMode
): EChartsOption {
  if (bars.length === 0) {
    return {
      graphic: [
        {
          type: 'text',
          left: 'center',
          top: 'middle',
          style: { text: 'No ownership data available', fontSize: 13, fill: colors.neutral[50] },
        },
      ],
    };
  }

  const roundByKey = new Map(rounds.map((r) => [r.key, r]));
  const positions = uniquePositions(bars);
  const colorByPosition = getColorByPosition(bars);
  const { axis: axisFormatter, value: valueFormatter } = MODE_FORMATTERS[mode];

  const series: BarSeriesOption[] = positions.map((position) => ({
    name: position,
    type: 'bar',
    stack: STACK_ID,
    data: bars.map((bar) => {
      const match = bar.positions.find((p) => p.position === position);
      return match ? metricValue(match, mode) : 0;
    }),
    itemStyle: { color: colorByPosition.get(position) },
  }));

  // Shade the column of the selected round. Always assign markArea (empty when nothing is
  // selected) so merge updates clear a previous highlight rather than leaving it stale.
  if (series.length > 0) {
    series[0].markArea = {
      silent: true,
      itemStyle: { color: colors.surface.selectedSubtle },
      data: selectedKey ? [[{ xAxis: selectedKey }, { xAxis: selectedKey }]] : [],
    };
  }

  return {
    grid: { top: 16, right: 8, bottom: 56, left: 8, containLabel: true },
    xAxis: {
      type: 'category',
      data: bars.map((b) => b.key),
      axisTick: { show: false },
      axisLabel: {
        fontSize: 11,
        color: colors.neutral[60],
        formatter: (key: string) => {
          const round = roundByKey.get(key);
          if (!round) {
            return key;
          }
          const dateStr = FMT.format('dateNumeric', dateToPlainDateString(round.date));
          return round.isCurrent ? `Current\n${dateStr}` : dateStr;
        },
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        fontSize: 11,
        color: colors.neutral[60],
        formatter: (v: number) => FMT.format(axisFormatter, v),
      },
      splitLine: { lineStyle: { color: colors.neutral[10] } },
    },
    legend: {
      // Paginate into a single scrollable row so many positions don't grow the legend
      // upward into the plot area.
      type: 'scroll',
      bottom: 0,
      itemWidth: 12,
      itemHeight: 8,
      textStyle: { fontSize: 12, color: colors.text.main },
    },
    tooltip: {
      // Item trigger so the formatter knows which stack segment is hovered; the full
      // per-position breakdown is rebuilt from the bar so the popup keeps every row.
      trigger: 'item',
      // Render on <body> so a tall popup (many positions) isn't clipped by the
      // overflow:hidden chart wrapper or its fixed height.
      appendToBody: true,
      // Default positioning clamps the popup inside the chart's view rect; anchor it near
      // the cursor and let it grow downward past the chart edge (appendToBody keeps it
      // unclipped) so a popup taller than the chart isn't cut off.
      position: (point, _params, _dom, _rect, size) => {
        const [cursorX, cursorY] = point;
        const [chartWidth] = size.viewSize;
        const [tooltipWidth] = size.contentSize;
        const left =
          cursorX + tooltipWidth + 16 <= chartWidth ? cursorX + 16 : Math.max(0, cursorX - tooltipWidth - 16);
        return [left, Math.max(0, cursorY)];
      },
      formatter: (params) => {
        const p = (Array.isArray(params) ? params[0] : params) as { dataIndex?: number; seriesName?: string };
        const bar = bars[p?.dataIndex ?? -1];
        if (!bar) {
          return '';
        }
        const round = roundByKey.get(bar.key);
        const header = round
          ? `${round.isCurrent ? 'Current — ' : ''}${FMT.format('dateNumeric', dateToPlainDateString(round.date))}`
          : bar.key;
        let total = 0;
        // series[0] is the bottom of the stack; reverse so the popup reads top-to-bottom.
        const rows = [...positions]
          .reverse()
          .map((name) => {
            const match = bar.positions.find((pos) => pos.position === name);
            return { name, value: match ? metricValue(match, mode) : 0 };
          })
          .filter((row) => row.value !== 0)
          .map((row) => {
            total += row.value;
            const marker = positionMarker(colorByPosition.get(row.name) ?? colors.neutral[50]);
            const valueText = FMT.format(valueFormatter, row.value);
            const hovered = row.name === p?.seriesName;
            // Shade and bold the row for the hovered stack segment.
            const text = hovered ? `<b>${row.name}: ${valueText}</b>` : `${row.name}: <b>${valueText}</b>`;
            const shade = hovered ? `background:${colors.surface.selectedSubtle};border-radius:3px;` : '';
            return `<div style="padding:1px 4px;${shade}">${marker}${text}</div>`;
          })
          .join('');
        return `<div style="font-size:12px"><b>${header}</b>${rows}<div style="padding:1px 4px">Total: <b>${FMT.format(
          valueFormatter,
          total
        )}</b></div></div>`;
      },
    },
    series,
  };
}

function positionMarker(color: string): string {
  return `<span style="display:inline-block;margin-right:4px;width:10px;height:10px;border-radius:50%;background:${color}"></span>`;
}

/**
 * Series colour per position, keyed the way the chart stacks them. Exported so the holdings grid's
 * lot swatches use the same colours instead of re-deriving a palette that would drift out of step.
 */
export function getColorByPosition(bars: IStackedBar[]): Map<string, string> {
  const positions = uniquePositions(bars);
  const palette = generateChartColourScheme(positions.length);

  return new Map(positions.map((position, i) => [position, palette[i % palette.length]]));
}

function uniquePositions(bars: IStackedBar[]): string[] {
  const seen = new Set<string>();
  const order: string[] = [];
  for (const bar of bars) {
    for (const { position } of bar.positions) {
      if (!seen.has(position)) {
        seen.add(position);
        order.push(position);
      }
    }
  }
  return order;
}
